<template>
	<div class="card">
		<div class="map-wrap">
			<div ref="map" class="map"></div>
			<div class="controls">
				<input v-model="query" @input="onFilter" type="search" placeholder="Filter by user, facility, OS, model…" />
				<button @click="resetFilter">Reset</button>
			</div>
			<div class="badge device-count">{{ plottedCount }} of {{ currentRows.length }} Devices Plotted</div>
			<div class="legend text-left">
				<div><span class="dot dot-good"></span>Battery ≥ 60%</div>
				<div><span class="dot dot-warn"></span>Battery 30–59%</div>
				<div><span class="dot dot-bad"></span>Battery &lt; 30%</div>
			</div>
		</div>
	</div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import { formatterFilter } from '@/utils/luxon-formater';
const DEFAULT_API_KEY = 'AIzaSyAhCWThHWyjbhS2s2qixFZLTdbkI4HqUGw';
const DEFAULT_MAP_ID = 'DEMO_MAP_ID';

export default {
	name: 'ViewTablets.vue',
	data () {
		return {
			apiKey: DEFAULT_API_KEY,
			mapId: DEFAULT_MAP_ID,
			initialCenter: { lat: 43.7, lng: -88.5 },
			initialZoom: 6,

			map: null,
			infoWindow: null,
			markers: [], // AdvancedMarkerElement[]
			clusterer: null, // MarkerClusterer
			query: '',
			currentRows: [], // DEVICES.slice(),
			plottedCount: 0,
			clustererReady: false,
			allRows: [],
		};
	},
	computed: {
		...mapState({
			tablets: (state) => state.tablets.tablets,
		}),
	},
	async mounted () {
		await this.getTablets();
		this.currentRows = this.tablets.data;
		this.allRows = this.tablets.data;
		this.ensureScripts()
			.then(() => {
				this.initMap();
				this.renderMarkers(this.currentRows);
				window.addEventListener('resize', this.onResize);
			})
			.catch({
				// console.error('Loader error:', err)
			});
	},

	beforeDestroy () {
		window.removeEventListener('resize', this.onResize);
		this.clearMarkersAndClusterer();
	},

	methods: {
		formatterFilter,
		...mapActions({
			getTablets: 'tablets/getTablets',
		}),
		async ensureScripts () {
			if (!(window.google && window.google.maps)) {
				await this.loadScript(
					`https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.apiKey)}&v=weekly&libraries=marker`,
					{ async: true, defer: true },
				);
			}
			if (!window.markerClusterer) {
				await this.loadScript('https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js');
			}
			this.clustererReady = !!window.markerClusterer;
		},
		loadScript (src, opts = {}) {
			return new Promise((resolve, reject) => {
				const existing = document.querySelector(`script[src="${src}"]`);
				if (existing) {
					if (existing.dataset.loaded === 'true') return resolve();
					existing.addEventListener('load', resolve);
					existing.addEventListener('error', reject);
					return;
				}
				const s = document.createElement('script');
				s.src = src;
				if (opts.async) s.async = true;
				if (opts.defer) s.defer = true;
				s.onload = () => {
					s.dataset.loaded = 'true';
					resolve();
				};
				s.onerror = reject;
				document.head.appendChild(s);
			});
		},

		initMap () {
			this.map = new window.google.maps.Map(this.$refs.map, {
				mapId: this.mapId,
				center: this.initialCenter,
				zoom: this.initialZoom,
				streetViewControl: false,
				fullscreenControl: true,
				mapTypeId: 'hybrid',
				mapTypeControl: true,
				mapTypeControlOptions: { style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU },
			});
			this.infoWindow = new window.google.maps.InfoWindow({
				headerDisabled: true,
			});
		},

		renderMarkers (rows) {
			if (!this.map) return;

			this.clearMarkersAndClusterer();

			const bounds = new window.google.maps.LatLngBounds();
			let validCount = 0;

			rows.forEach((row) => {
				const meta = this.safeParse(row.tabletAdditionalData);
				const lat = Number(meta?.latitude);
				const lng = Number(meta?.longitude);
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

				const color = this.colorForBattery(row.batteryLevel);

				const marker = new window.google.maps.marker.AdvancedMarkerElement({
					position: { lat, lng },
					map: this.map, // setting map is fine even with clusterer
					title: row.userName || 'Device',
					content: this.createMarkerDot(color),
				});

				marker.addListener('gmp-click', () => {
					const info = this.buildInfoContent(row, meta);
					this.infoWindow.setContent(info.html);
					this.infoWindow.open({ map: this.map, anchor: marker });
					// Attach close button event after InfoWindow is rendered
					window.setTimeout(() => {
						const btn = document.getElementById(info.closeId);
						if (btn) {
							btn.onclick = () => this.infoWindow.close();
						}
					}, 0);
					// Click-to-zoom behavior
					this.map.panTo(marker.position);
					const targetZoom = 14;
					if (this.map.getZoom() < targetZoom) this.map.setZoom(targetZoom);
				});

				this.markers.push(marker);
				bounds.extend(marker.position);
				validCount++;
			});

			// Clustering
			if (this.clustererReady && this.markers.length) {
				this.clusterer = new window.markerClusterer.MarkerClusterer({
					map: this.map,
					markers: this.markers,
					// algorithm: new window.markerClusterer.GridAlgorithm({ maxZoom: 16, radius: 60 }),
				});
			}
			this.plottedCount = validCount;
			if (!bounds.isEmpty() && validCount > 0) {
				this.map.fitBounds(bounds, { top: 64, right: 16, bottom: 16, left: 16 });
			}
		},

		clearMarkersAndClusterer () {
			if (this.clusterer) {
				this.clusterer.setMap(null);
				this.clusterer = null;
			}
			this.markers.forEach((m) => (m.map = null));
			this.markers = [];
		},

		// --- UI ---
		onFilter () {
			const q = (this.query || '').trim().toLowerCase();
			if (!q) {
				this.currentRows = this.allRows.slice();
			} else {
				this.currentRows = this.allRows.filter((d) => {
					const meta = this.safeParse(d.tabletAdditionalData) || {};
					const hay = [
						d.userName,
						d.hospitalName,
						d.os,
						d.appVersion,
						meta.deviceModel,
						meta.deviceManufacturer,
						meta.networkType,
						d.serialNumber,
					]
						.filter(Boolean)
						.join(' ')
						.toLowerCase();
					return hay.includes(q);
				});
			}
			this.renderMarkers(this.currentRows);
		},
		resetFilter () {
			this.query = '';
			this.onFilter();
		},

		onResize () {
			const b = this.computeBounds(this.markers);
			if (b) this.map.fitBounds(b, { top: 64, right: 16, bottom: 16, left: 16 });
		},

		computeBounds (ms) {
			const bounds = new window.google.maps.LatLngBounds();
			let expanded = false;
			ms.forEach((m) => {
				if (m.position) {
					bounds.extend(m.position);
					expanded = true;
				}
			});
			return expanded ? bounds : null;
		},

		// --- helpers ---
		safeParse (str) {
			if (!str) return null;
			try {
				return JSON.parse(str);
			} catch {
				return null;
			}
		},
		colorForBattery (pct) {
			if (pct >= 60) return '#2E7D32';
			if (pct >= 30) return '#F9A825';
			return '#C62828';
		},
		escapeHTML (val) {
			return String(val ?? '').replace(
				/[&<>"']/g,
				(c) =>
					({
						'&': '&amp;',
						'<': '&lt;',
						'>': '&gt;',
						'"': '&quot;',
						'\'': '&#039;',
					}[c]),
			);
		},
		createMarkerDot (color) {
			const div = document.createElement('div');
			div.style.width = '16px';
			div.style.height = '16px';
			div.style.borderRadius = '50%';
			div.style.background = color;
			div.style.border = '2px solid white';
			div.style.boxShadow = '0 0 0 1px rgba(0,0,0,.2)';
			return div;
		},
		buildInfoContent (row, meta) {
			const closeId = `infowindow-close-${Math.random().toString(36).substring(2, 9)}`;
			const html = [
				`<div style="font-weight:600;font-size:14px;margin-bottom:4px;display:flex;align-items:center;justify-content:space-between;text-align:left">
					   <span>${this.escapeHTML(row.userName || 'Unknown user')}</span>
					   <button id="${closeId}" style="margin-left:8px;background:none;border:none;cursor:pointer;font-size:28px;width:36px;height:36px;line-height:1;display:flex;align-items:center;justify-content:center;">&times;</button>
				   </div>`,
				`<div style="text-align:left"><strong>Facility:</strong> ${this.escapeHTML(row.hospitalName || '-')}
			(ID ${row.hospitalId})
		   </div>`,
				`<div style="text-align:left">
			<strong>OS:</strong>${this.escapeHTML(row.os || '-')}&nbsp;
			<strong>Battery:</strong>${row.batteryLevel}%
			 </div>`,
				`<div style="text-align:left"><strong>App:</strong> ${this.escapeHTML(row.appVersion || '-')}</div>`,
			];
			if (meta?.anyDeskId) {
				html.push(
					`<div style="text-align:left"><strong>AnyDesk:</strong> 
						${this.escapeHTML(meta.anyDeskId)}
					</div>`,
				);
			}
			html.push(
				`<div style="text-align:left"><strong>Model:</strong>
				${this.escapeHTML(meta?.deviceModel || '-')}
			&nbsp; <strong>Maker:</strong> ${this.escapeHTML(meta?.deviceManufacturer || '-')}</div>`,
				`<div style="text-align:left"><strong>Build:</strong> ${this.escapeHTML(meta?.buildNumber || '-')}</div>`,
				`<div style="text-align:left"><strong>Sec. Patch:</strong>
			${this.escapeHTML(meta?.securityPatch || '-')}
			&nbsp; <strong>SDK:</strong> ${this.escapeHTML(String(meta?.sdkInt ?? '-'))}</div>`,
				`<div style="text-align:left"><strong>Network:</strong>
			${this.escapeHTML(meta?.networkType || '-')} (${this.escapeHTML(meta?.ipAddress || '-')})
		   </div>`,
				`<div style="text-align:left"><strong>Reported:</strong>
          ${row.reportTimestamp ? this.escapeHTML(this.formatterFilter(row.reportTimestamp, 'datetime_med')) : '-'}
        </div>`,
			);
			return { html: `<div style="max-width:320px">${html.join('')}</div>`, closeId };
		},
	},
};
</script>

<style scoped>
.card {
	background: #fff;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
	overflow: hidden;
}

.title {
	font-weight: 700;
	font-size: 18px;
}

.badge {
	font-size: 12px;
	padding: 3px 8px;
	background: #eef2ff;
	border-radius: 999px;
}

.controls {
	position: absolute;
	top: 10px;
	right: 60px;
	margin-left: auto;
	display: flex;
	gap: 8px;
	align-items: center;
}

.controls input[type='search'] {
	padding: 8px 10px;
	height: 40px;
	border: 1px solid #fff;
	background-color: #fff;
	outline: none;
}

.map {
	width: 100%;
	height: calc(100vh - 60px);
}

.device-count {
	position: absolute;
	left: 12px;
	bottom: 94.4px;
}

.legend {
	position: absolute;
	bottom: 12px;
	left: 12px;
	background: #fff;
	border-radius: 12px;
	padding: 10px 12px;
	box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
	font-size: 12px;
	line-height: 1.4;
}

.legend .dot {
	display: inline-block;
	width: 10px;
	height: 10px;
	margin-right: 6px;
	border-radius: 999px;
	border: 1px solid #fff;
	box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
}

.dot-good {
	background: #2e7d32;
}

.dot-warn {
	background: #f9a825;
}

.dot-bad {
	background: #c62828;
}

.map-wrap {
	position: relative;
}

button {
	padding: 8px 12px;
	height: 40px;
	border: 1px solid #ddd;
	background: #fff;
	cursor: pointer;
}

button:hover {
	background: #f3f4f6;
}
</style>
